import React from 'react';
import { Card, PageHeader, Button, LoadingSpinner, EmptyState } from './UnifiedUIComponents';
import { FaArrowLeft } from 'react-icons/fa';

/**
 * Reusable Page Template Component
 * Standardizes layout, styling, and common page behaviors
 * Used across all pages for consistency
 */
export const PageTemplate = ({
  title,
  subtitle = null,
  icon = null,
  headerAction = null,
  onBack = null,
  loading = false,
  error = null,
  isEmpty = false,
  emptyStateIcon = null,
  emptyStateMessage = 'No data available',
  emptyStateAction = null,
  children,
  className = '',
  containerSize = 'large',
  showBreadcrumb = false,
  breadcrumbs = []
}) => {
  const containerClasses = {
    small: 'container-small',
    medium: 'container-medium',
    large: 'container-large',
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  if (loading) {
    return (
      <div className="page-template" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70vh',
        padding: '2rem 1rem'
      }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-template" style={{
        padding: '2rem 1rem',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <Card size="lg" style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.02) 100%)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 'var(--fs-3xl)', marginBottom: '1rem', color: 'var(--danger-color)' }}>⚠️</div>
          <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0' }}>Error Loading Page</h2>
          <p style={{ color: 'var(--text-secondary)', margin: 0, marginBottom: '1.5rem' }}>
            {error}
          </p>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload Page
          </Button>
        </Card>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="page-template" style={{
        padding: '2rem 1rem',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <PageHeader title={title} subtitle={subtitle} icon={icon} action={headerAction} />
        <EmptyState
          icon={emptyStateIcon}
          title={emptyStateMessage}
          action={emptyStateAction}
        />
      </div>
    );
  }

  return (
    <div className={`page-template ${className}`} style={{
      padding: '2rem 1rem',
      maxWidth: containerSize === 'large' ? '1400px' : containerSize === 'medium' ? '1000px' : '800px',
      margin: '0 auto'
    }}>
      {/* Breadcrumb if needed */}
      {showBreadcrumb && breadcrumbs.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          fontSize: 'var(--fs-base)',
          color: 'var(--text-secondary)'
        }}>
          {breadcrumbs.map((crumb, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span style={{ color: 'var(--text-light)' }}>›</span>}
              <span
                onClick={crumb.onClick}
                style={{
                  cursor: crumb.onClick ? 'pointer' : 'default',
                  color: crumb.onClick ? 'var(--primary-color)' : 'var(--text-secondary)',
                  transition: 'color var(--transition-base)',
                  ':hover': { color: 'var(--primary-dark)' }
                }}
                onMouseEnter={(e) => {
                  if (crumb.onClick) {
                    e.currentTarget.style.color = 'var(--primary-dark)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (crumb.onClick) {
                    e.currentTarget.style.color = 'var(--primary-color)';
                  }
                }}
              >
                {crumb.label}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Page Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '1.5rem',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: '280px' }}>
          {onBack && (
            <Button
              variant="ghost"
              onClick={handleBack}
              style={{ minWidth: 'auto', padding: '0.5rem' }}
            >
              <FaArrowLeft />
            </Button>
          )}
          <PageHeader
            title={title}
            subtitle={subtitle}
            icon={icon}
          />
        </div>
        {headerAction && (
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {headerAction}
          </div>
        )}
      </div>

      {/* Page Content */}
      <div className="page-content">
        {children}
      </div>
    </div>
  );
};

/**
 * Reusable Table Page Component
 * For pages that display tables with filtering, sorting, pagination
 */
export const TablePageTemplate = ({
  title,
  subtitle,
  icon,
  tableColumns,
  tableData,
  loading,
  error,
  onAdd = null,
  onEdit = null,
  onDelete = null,
  searchFields = [],
  ...pageProps
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [sortConfig, setSortConfig] = React.useState({ key: null, direction: 'asc' });
  const [selectedRows, setSelectedRows] = React.useState(new Set());

  const filteredData = React.useMemo(() => {
    if (!tableData || !searchTerm) return tableData || [];
    
    const term = searchTerm.toLowerCase();
    return tableData.filter(row =>
      searchFields.some(field =>
        row[field]?.toString().toLowerCase().includes(term)
      )
    );
  }, [tableData, searchTerm, searchFields]);

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  return (
    <PageTemplate
      title={title}
      subtitle={subtitle}
      icon={icon}
      loading={loading}
      error={error}
      isEmpty={sortedData.length === 0}
      headerAction={onAdd && <Button variant="primary" onClick={onAdd}>Add New</Button>}
      {...pageProps}
    >
      {/* Search Bar */}
      {searchFields.length > 0 && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              border: '1px solid var(--border-light)',
              fontSize: 'var(--fs-lg)',
              transition: 'all var(--transition-base)',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--primary-color)';
              e.target.style.boxShadow = '0 0 0 3px var(--primary-ultralight)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--border-light)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </Card>
      )}

      {/* Table Container */}
      <Card className="table-card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                {tableColumns.map(col => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && setSortConfig({
                      key: col.key,
                      direction: sortConfig.key === col.key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                    })}
                    style={{
                      cursor: col.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      textAlign: col.align || 'left',
                      width: col.width
                    }}
                  >
                    {col.label}
                    {col.sortable && sortConfig.key === col.key && (
                      <span style={{ marginLeft: '0.5rem' }}>
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                ))}
                {(onEdit || onDelete) && <th style={{ textAlign: 'center', width: '120px' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row, idx) => (
                <tr key={idx}>
                  {tableColumns.map(col => (
                    <td
                      key={col.key}
                      style={{ textAlign: col.align || 'left' }}
                    >
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  ))}
                  {(onEdit || onDelete) && (
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        {onEdit && (
                          <Button variant="secondary" size="sm" onClick={() => onEdit(row)}>
                            Edit
                          </Button>
                        )}
                        {onDelete && (
                          <Button variant="danger" size="sm" onClick={() => onDelete(row)}>
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Results Info */}
      <div style={{
        marginTop: '1rem',
        color: 'var(--text-secondary)',
        fontSize: 'var(--fs-base)'
      }}>
        Showing {sortedData.length} of {tableData?.length || 0} results
      </div>
    </PageTemplate>
  );
};

export default PageTemplate;
